import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ============================================================================
// Typography Variants
// ============================================================================
type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'p'
  | 'blockquote'
  | 'inline-code'
  | 'lead'
  | 'large'
  | 'small'
  | 'small-semibold'
  | 'muted'
  | 'menubar';

// Default element mapping for each variant
const defaultElementMap: Record<TypographyVariant, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  p: 'p',
  blockquote: 'blockquote',
  'inline-code': 'code',
  lead: 'p',
  large: 'div',
  small: 'small',
  'small-semibold': 'p',
  muted: 'p',
  menubar: 'p',
};

// Variant styles mapping
const variantStyles: Record<TypographyVariant, string> = {
  h1: cn(
    'scroll-m-20 font-extrabold tracking-tight',
    'text-4xl leading-[40px]',
    'md:text-5xl md:leading-[48px]',
  ),
  h2: cn(
    'scroll-m-20 pb-0 font-semibold tracking-tight',
    'text-2xl leading-8',
    'md:text-3xl md:leading-9',
  ),
  h3: cn(
    'scroll-m-20 font-semibold tracking-tight',
    'text-xl leading-7',
    'md:text-2xl md:leading-8',
  ),
  h4: cn(
    'scroll-m-20 font-semibold tracking-tight',
    'text-lg leading-7',
    'md:text-xl',
  ),
  p: 'leading-6',
  blockquote: 'mt-6 border-l-2 pl-6 italic',
  'inline-code':
    'relative rounded-sm bg-muted px-[0.3rem] py-[0.2rem] font-mono font-normal text-sm',
  lead: 'text-muted-foreground text-xl leading-7',
  large: 'font-semibold text-lg leading-7',
  small: 'font-medium text-sm leading-5',
  'small-semibold': 'font-medium text-sm leading-5 font-semibold',
  muted: 'text-muted-foreground text-sm leading-5',
  menubar: 'text-xs leading-3 text-accent-foreground',
};

// ============================================================================
// Main Typography Component
// ============================================================================
type TypographyProps<T extends ElementType = ElementType> = {
  variant?: TypographyVariant;
  as?: T;
  children: ReactNode;
  className?: string;
};

export function Typography<T extends ElementType = ElementType>({
  variant = 'p',
  as,
  children,
  className,
  ...props
}: TypographyProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof TypographyProps<T>>) {
  const Component = as || defaultElementMap[variant];

  return (
    <Component
      className={cn(variantStyles[variant], className)}
      type={as === 'button' ? 'button' : undefined}
      {...props}
    >
      {children}
    </Component>
  );
}
